import {
  MARKET_CONDITION_MEANINGS,
  MARKET_CONDITION_V3_VALUES,
  formatMarketConditionLabel,
  normalizeMarketConditionToken,
  type MarketConditionV3Value,
} from "./marketConditionRegime";

/** Solid brand hex used for legend swatches (pre-alpha). */
export const REGIME_OVERLAY_SOLID: Record<MarketConditionV3Value, string> = {
  uptrend: "#06E363",
  downtrend: "#E00101",
  range: "#6B7280",
  choppy: "#8B5CF6",
  unknown: "#455A64",
};

/** Fill opacity for chart bgcolor (~7%) — keep subtle so candles/trades stay readable. */
const REGIME_OVERLAY_ALPHA = 0.07;

function hexToRgba(hex: string, alpha: number): string {
  const raw = hex.replace("#", "").trim();
  const full =
    raw.length === 3
      ? raw
          .split("")
          .map((c) => c + c)
          .join("")
      : raw;
  const n = Number.parseInt(full, 16);
  if (!Number.isFinite(n) || full.length !== 6) {
    return `rgba(69, 90, 100, ${alpha})`;
  }
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Translucent fill for Trade Review regime histogram bgcolor.
 * Unknown / legacy / empty tokens use the unknown palette.
 */
export function regimeOverlayFillRgba(condition: string | null | undefined): string {
  const token = normalizeMarketConditionToken(condition);
  const solid =
    token && token in REGIME_OVERLAY_SOLID
      ? REGIME_OVERLAY_SOLID[token as MarketConditionV3Value]
      : REGIME_OVERLAY_SOLID.unknown;
  return hexToRgba(solid, REGIME_OVERLAY_ALPHA);
}

export type RegimeOverlayLegendItem = {
  value: MarketConditionV3Value;
  label: string;
  solid: string;
  meaning: string;
};

/** Ordered v3 legend for the Regime overlay toggle. */
export const REGIME_OVERLAY_LEGEND_ITEMS: readonly RegimeOverlayLegendItem[] =
  MARKET_CONDITION_V3_VALUES.map((value) => ({
    value,
    label: formatMarketConditionLabel(value),
    solid: REGIME_OVERLAY_SOLID[value],
    meaning: MARKET_CONDITION_MEANINGS[value] ?? "",
  }));
